import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Shield, AlertTriangle } from "lucide-react";
import { useRetainageReleases, useCreateRetainageRelease } from "@/hooks/useRetainageReleases";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  workOrder: any;
  billedToDate: number;
  onMutate: () => void;
}

const RELEASE_TYPES = [
  { value: "partial", label: "Partial Release" },
  { value: "final", label: "Final Release" },
  { value: "early", label: "Early Release" },
  { value: "per_unit", label: "Per Unit" },
  { value: "negotiated", label: "Negotiated" },
];

const RELEASE_REASONS = [
  { value: "substantial_completion", label: "Substantial Completion" },
  { value: "punch_complete", label: "Punch List Complete" },
  { value: "lien_waiver_received", label: "Lien Waiver Received" },
  { value: "gc_released", label: "GC Released Retainage" },
  { value: "per_unit_accepted", label: "Per Unit Accepted" },
  { value: "negotiated", label: "Negotiated" },
  { value: "court_ordered", label: "Court Ordered" },
];

export const WORetainageTab = ({ workOrder, billedToDate, onMutate }: Props) => {
  const { data: releases } = useRetainageReleases(workOrder.id);
  const createRelease = useCreateRetainageRelease();
  const [modalOpen, setModalOpen] = useState(false);

  const retainagePct = Number(workOrder.retainage_pct ?? 10);
  const contractAmount = Number(workOrder.amount);
  const totalHeld = billedToDate * (retainagePct / 100);
  const totalReleased = Number(workOrder.retainage_released ?? 0);
  const balance = Math.max(0, totalHeld - totalReleased);

  // Release form state
  const [releaseType, setReleaseType] = useState("final");
  const [releaseAmount, setReleaseAmount] = useState("");
  const [releaseReason, setReleaseReason] = useState("");
  const [lienWaiver, setLienWaiver] = useState(false);
  const [punchList, setPunchList] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = () => {
    setReleaseType("final");
    setReleaseAmount(balance.toFixed(2));
    setReleaseReason("");
    setLienWaiver(false);
    setPunchList(false);
    setNotes("");
    setModalOpen(true);
  };

  const canRelease = lienWaiver || punchList;
  const amountNum = Number(releaseAmount) || 0;

  const handleRelease = async () => {
    if (!releaseReason) return toast.error("Select a release reason");
    if (!canRelease) return toast.error("At least one checkbox must be checked");
    if (amountNum <= 0) return toast.error("Amount must be greater than 0");
    if (amountNum > balance + 0.01) return toast.error("Amount exceeds retainage balance");

    setBusy(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) { setBusy(false); return toast.error("Company not found"); }

    try {
      await createRelease.mutateAsync({
        company_id: companyId,
        work_order_id: workOrder.id,
        release_amount: amountNum,
        release_type: releaseType,
        release_reason: releaseReason,
        lien_waiver_received: lienWaiver,
        punch_list_complete: punchList,
        notes: notes || undefined,
      });
      toast.success(`Released ${fmt(amountNum)} retainage`);
      setModalOpen(false);
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const statusColor: Record<string, string> = {
    holding: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    partial_release: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    released: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    disputed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    waived: "bg-muted text-muted-foreground",
  };

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" /> Retainage Summary
            <Badge className={statusColor[workOrder.retainage_status ?? "holding"] ?? ""}>
              {(workOrder.retainage_status ?? "holding").replace(/_/g, " ")}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total Held</p>
              <p className="text-lg font-semibold">{fmt(totalHeld)}</p>
              <p className="text-xs text-muted-foreground">{retainagePct}% of {fmt(contractAmount)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Released</p>
              <p className="text-lg font-semibold text-green-600">{fmt(totalReleased)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Balance</p>
              <p className="text-lg font-semibold">{fmt(balance)}</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2 text-sm">
            <p className="font-medium text-muted-foreground">Requirements before release:</p>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">☐</span>
              <span>Punch list complete</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">☐</span>
              <span>Lien waiver received</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">☐</span>
              <span>GC has released company retainage</span>
            </div>
          </div>

          {balance > 0 && (
            <Button onClick={openModal} className="w-full">
              Release Retainage →
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Release History */}
      {(releases ?? []).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Release History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(releases ?? []).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{format(new Date(r.created_at), "MMM d, yyyy")}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{r.release_type}</Badge></TableCell>
                    <TableCell className="text-sm capitalize">{r.release_reason?.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-right font-medium text-green-600">{fmt(Number(r.release_amount))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Release Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Release Retainage</DialogTitle>
            <DialogDescription>
              {workOrder.number} — Balance: {fmt(balance)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Release Type</Label>
              <Select value={releaseType} onValueChange={setReleaseType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELEASE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={balance}
                value={releaseAmount}
                onChange={e => setReleaseAmount(e.target.value)}
              />
              {amountNum > balance && (
                <p className="text-xs text-destructive mt-1">Exceeds balance</p>
              )}
            </div>

            <div>
              <Label>Reason</Label>
              <Select value={releaseReason} onValueChange={setReleaseReason}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {RELEASE_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="lien"
                  checked={lienWaiver}
                  onCheckedChange={(v) => setLienWaiver(v === true)}
                />
                <Label htmlFor="lien">Lien waiver received</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="punch"
                  checked={punchList}
                  onCheckedChange={(v) => setPunchList(v === true)}
                />
                <Label htmlFor="punch">Punch list complete</Label>
              </div>

              {!canRelease && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 text-sm">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                  <span>At least one checkbox must be checked before releasing</span>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRelease}
              disabled={busy || !canRelease || !releaseReason || amountNum <= 0 || amountNum > balance + 0.01}
            >
              {busy ? "Releasing..." : `Release ${fmt(amountNum)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
